import { useState, useEffect } from 'react';
import { apiClient, type MeResponse } from '@/lib/api/client';
import { WorkspaceSwitcher } from '@/features/workspace/WorkspaceSwitcher';
import { SettingsDrawer } from '@/features/workspace/SettingsDrawer';
import { NewWorkspaceModal } from '@/features/workspace/NewWorkspaceModal';
import { ProjectsList } from '@/features/project/ProjectsList';
import { NewProjectModal } from '@/features/project/NewProjectModal';
import { ProjectSettingsDrawer } from '@/features/project/ProjectSettingsDrawer';
import { ProjectView } from '@/features/project/ProjectView';
import { AllProjectsView } from '@/features/project/AllProjectsView';
import { SourcesView } from '@/features/source/SourcesView';
import { InboxView } from '@/features/inbox/InboxView';
import { TelegramModal } from '@/features/telegram/TelegramModal';
import { UiZoomControl } from '@/components/UiZoom';
import { ProfileModal } from '@/features/profile/ProfileModal';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.apetech.studio';
import { cn } from '@/lib/utils';
import { CommandPalette, type CommandAction } from '@/features/command/CommandPalette';

interface Props {
  me: MeResponse;
  onSignOut: () => void;
  onReload: () => void;
}

export function Dashboard({ me, onSignOut, onReload }: Props) {
  const [view, setView] = useState<
    'workspace' | 'all-projects' | 'sources' | 'inbox' | { kind: 'project'; project_id: string }
  >('workspace');
  const [signingOut, setSigningOut] = useState(false);
  const [wsSettingsOpen, setWsSettingsOpen] = useState(false);
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tgModalOpen, setTgModalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  // Cmd+K / Ctrl+K → open command palette. Attached at document level so it
  // works regardless of which view is mounted. Escape lives inside the
  // palette itself.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const activeWs =
    me.workspaces.find((w) => w.ws_id === me.active_ws_id) ?? me.workspaces[0] ?? null;

  // After `me` reloads from the parent (e.g., post-invite-accept), nudge the
  // ProjectsList to refetch. ProjectsList already depends on activeWsId, but
  // in cases where ws_id stayed the same (just a pin was added) we still need
  // a manual nudge.
  useEffect(() => {
    setRefreshKey((k) => k + 1);
  }, [me.user.user_id, me.workspaces.length, me.active_ws_id]);

  async function signOut() {
    setSigningOut(true);
    try {
      await apiClient.logout();
    } catch {
      /* ignore */
    }
    onSignOut();
  }

  function bump() {
    setRefreshKey((k) => k + 1);
  }

  // Discriminator for active-state styling (was hardcoded "Inbox is active",
  // which lied when view === 'workspace' or any other view).
  const viewKey: 'workspace' | 'all-projects' | 'sources' | 'inbox' | 'project' =
    typeof view === 'object' ? 'project' : view;

  // Body content per view. Header below renders unconditionally so the
  // workspace switcher + nav are always present (previously each early
  // return dropped the entire shell, leaving users stranded).
  const body =
    viewKey === 'all-projects' ? (
      <AllProjectsView
        me={me}
        onBack={() => setView('workspace')}
        onOpenProject={(id) => setView({ kind: 'project', project_id: id })}
        onPinChanged={() => {
          bump();
          onReload();
        }}
      />
    ) : viewKey === 'sources' ? (
      <SourcesView
        onBack={() => setView('workspace')}
        workspaceName={activeWs?.name ?? 'Workspace'}
      />
    ) : viewKey === 'inbox' ? (
      <InboxView onBack={() => setView('workspace')} />
    ) : viewKey === 'project' && typeof view === 'object' ? (
      <ProjectView
        projectId={view.project_id}
        myUserId={me.user.user_id}
        onBack={() => {
          setView('workspace');
          bump();
        }}
      />
    ) : (
      <div className="mx-auto max-w-6xl px-6 pt-16 pb-10">
        <div className="space-y-1">
          {/* H1 + paired Edit button. The "Workspace" eyebrow above the h1
              that NAMES the workspace was redundant labeling (per DESIGN
              principle: distill). The slug+email line below provides
              context if needed. */}
          <div className="group inline-flex items-center gap-2 text-balance">
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
              {activeWs?.name ?? 'No workspace'}
            </h1>
            {activeWs && (
              <button
                type="button"
                onClick={() => setWsSettingsOpen(true)}
                aria-label={`Edit workspace ${activeWs.name}`}
                className="rounded-md border border-border/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70 transition-colors duration-150 hover:border-ring/60 hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
              >
                Edit
              </button>
            )}
          </div>
          {activeWs && (
            <p className="font-mono text-xs text-muted-foreground">
              /w/{activeWs.slug} · {me.user.email}
            </p>
          )}
        </div>
        <div className="mt-10">
          <ProjectsList
            refreshKey={refreshKey}
            activeWsId={activeWs?.ws_id ?? null}
            activeWorkspace={activeWs}
            onOpenProject={(id) => setView({ kind: 'project', project_id: id })}
            onNewProject={() => setNewProjectOpen(true)}
          />
        </div>
      </div>
    );

  /** Nav pill — active state via a 2px underline indicator, not a filled
   *  pill. Saved bg-primary fills for write-actions (Add, Continue). The
   *  underline reads as "current location" — pill fill read as "primary
   *  action" and competed for color budget. */
  const NavPill = ({
    label,
    active,
    onClick,
    ariaLabel,
    title,
  }: {
    label: string;
    active: boolean;
    onClick: () => void;
    ariaLabel?: string;
    title?: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={ariaLabel}
      title={title}
      className={cn(
        'relative rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-150',
        active
          ? 'text-foreground after:absolute after:inset-x-3 after:-bottom-[14px] after:h-px after:bg-primary'
          : 'text-muted-foreground hover:bg-card hover:text-foreground',
      )}
    >
      {label}
    </button>
  );

  return (
    <main className="min-h-dvh motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150 motion-safe:ease-out bg-background text-foreground antialiased">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm">Skip to content</a>
      <header className="border-b border-border bg-card/40">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-y-2 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView('workspace')}
              className="flex items-center gap-3 rounded-md px-1 -mx-1 transition-opacity hover:opacity-80"
              aria-label="Home — workspace projects"
            >
              {/* Big bold OPs wordmark. Replaces the wordmark logo —
                  pulses in the brand glow color so it doubles as a tiny
                  brand moment in the nav. */}
              <span className="ops-pulse text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                OPs
              </span>
            </button>
          </div>
          <nav className="flex flex-wrap items-center justify-end gap-y-1 gap-x-1.5 sm:gap-x-2" aria-label="Primary">
            <NavPill label="Inbox" active={viewKey === 'inbox'} onClick={() => setView('inbox')} />
            <NavPill label="Sources" active={viewKey === 'sources'} onClick={() => setView('sources')} />
            <NavPill label="All projects" active={viewKey === 'all-projects'} onClick={() => setView('all-projects')} />
            <button
              onClick={() => setTgModalOpen(true)}
              aria-haspopup="dialog"
              className="rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:bg-card hover:text-foreground"
              aria-label="Telegram delivery settings"
              title="Telegram delivery"
            >
              Telegram
            </button>
            <div className="mx-1 h-5 w-px bg-border" aria-hidden />
            <WorkspaceSwitcher
              me={me}
              onSwitched={() => {
                // Switching workspaces should land the user on the
                // workspace home (projects list) — staying on Inbox /
                // Sources / a specific Project from the previous
                // workspace is disorienting and, in the Project case,
                // shows data from the old workspace.
                setView('workspace');
                onReload();
              }}
              onCreate={() => setNewWsOpen(true)}
            />
            <div role="group" aria-label="Account" className="flex items-center gap-1">
              {/* Profile button — shows avatar (or initial) and opens
                  the Profile modal where the user edits name + image. */}
              <button
                onClick={() => setProfileOpen(true)}
                className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-primary/15 text-xs font-semibold text-primary transition-opacity hover:opacity-90"
                aria-label="Edit your profile"
                title={me.user.name || me.user.email}
              >
                {me.user.image_key ? (
                  <img
                    src={`${API_BASE}/v1/files/${me.user.image_key}?v=${encodeURIComponent(me.user.image_key)}`}
                    alt=""
                    width={32}
                    height={32}
                    className="block h-8 w-8 rounded-full object-cover"
                  />
                ) : me.user.avatar_url ? (
                  <img
                    src={me.user.avatar_url}
                    alt=""
                    width={32}
                    height={32}
                    className="block h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  (me.user.name || me.user.email)[0]?.toUpperCase()
                )}
              </button>
              <button
                onClick={() => setWsSettingsOpen(true)}
                className="grid size-8 place-items-center rounded-full text-muted-foreground transition-colors duration-150 hover:bg-card hover:text-foreground"
                aria-label="Workspace settings"
                title="Workspace settings"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              <button
                onClick={signOut}
                disabled={signingOut}
                className="grid size-8 place-items-center rounded-full text-muted-foreground transition-colors duration-150 hover:bg-card hover:text-foreground disabled:opacity-60"
                aria-label={signingOut ? 'Signing out' : 'Sign out'}
                title="Sign out"
              >
                {signingOut ? (
                  <div className="size-3.5 animate-pulse rounded-full bg-muted-foreground" aria-hidden />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                )}
              </button>
            </div>
          </nav>
        </div>
      </header>

      <div id="main-content">{body}</div>

      {wsSettingsOpen && (
        <SettingsDrawer
          me={me}
          onClose={() => setWsSettingsOpen(false)}
          onChanged={onReload}
        />
      )}
      {newWsOpen && (
        <NewWorkspaceModal
          onClose={() => setNewWsOpen(false)}
          onCreated={() => {
            setNewWsOpen(false);
            onReload();
          }}
        />
      )}
      {newProjectOpen && (
        <NewProjectModal
          onClose={() => setNewProjectOpen(false)}
          onCreated={(id) => {
            setNewProjectOpen(false);
            // Open the new project so you can immediately start adding tasks
            setView({ kind: 'project', project_id: id });
          }}
        />
      )}
      {openProjectId && (
        <ProjectSettingsDrawer
          projectId={openProjectId}
          myUserId={me.user.user_id}
          onClose={() => setOpenProjectId(null)}
          onChanged={() => bump()}
          onDeleted={() => {
            setOpenProjectId(null);
            bump();
          }}
        />
      )}
      {tgModalOpen && <TelegramModal onClose={() => setTgModalOpen(false)} />}
      {profileOpen && (
        <ProfileModal
          me={me}
          onClose={() => setProfileOpen(false)}
          onSaved={onReload}
        />
      )}

      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        actions={buildCommands({
          activeWsName: activeWs?.name ?? null,
          workspaces: me.workspaces,
          onGoInbox: () => setView('inbox'),
          onGoSources: () => setView('sources'),
          onGoAllProjects: () => setView('all-projects'),
          onGoWorkspace: () => setView('workspace'),
          onNewProject: () => setNewProjectOpen(true),
          onNewWorkspace: () => setNewWsOpen(true),
          onOpenSettings: () => setWsSettingsOpen(true),
          onOpenTelegram: () => setTgModalOpen(true),
          onSwitchWorkspace: async (ws_id) => {
            await apiClient.setActiveWorkspace(ws_id);
            setView('workspace');
            onReload();
          },
          onSignOut: signOut,
        })}
      />

      <UiZoomControl />
    </main>
  );
}

/** Build the standard action list for the palette. Kept outside the component
 *  so the array identity is stable across re-renders (the palette useMemo's
 *  the filter result on `actions`, so referential stability avoids needless
 *  recomputation). */
function buildCommands(args: {
  activeWsName: string | null;
  workspaces: MeResponse['workspaces'];
  onGoInbox: () => void;
  onGoSources: () => void;
  onGoAllProjects: () => void;
  onGoWorkspace: () => void;
  onNewProject: () => void;
  onNewWorkspace: () => void;
  onOpenSettings: () => void;
  onOpenTelegram: () => void;
  onSwitchWorkspace: (ws_id: string) => Promise<void> | void;
  onSignOut: () => void;
}): CommandAction[] {
  const cmds: CommandAction[] = [
    {
      id: 'go-inbox',
      label: 'Go to Inbox',
      hint: 'Triage events',
      section: 'Navigate',
      keywords: ['home', 'triage', 'events'],
      run: args.onGoInbox,
    },
    {
      id: 'go-workspace',
      label: 'Go to Workspace home',
      hint: args.activeWsName ?? undefined,
      section: 'Navigate',
      keywords: ['projects', 'home'],
      run: args.onGoWorkspace,
    },
    {
      id: 'go-sources',
      label: 'Go to Sources',
      hint: 'Configure event ingestion',
      section: 'Navigate',
      keywords: ['integrations', 'api', 'keys', 'webhook'],
      run: args.onGoSources,
    },
    {
      id: 'go-all-projects',
      label: 'Go to All Projects',
      hint: 'Cross-workspace view',
      section: 'Navigate',
      keywords: ['list', 'shared'],
      run: args.onGoAllProjects,
    },
    {
      id: 'new-project',
      label: 'New project',
      hint: args.activeWsName ? `In ${args.activeWsName}` : undefined,
      section: 'Create',
      shortcut: 'P',
      keywords: ['add', 'create'],
      run: args.onNewProject,
    },
    {
      id: 'new-workspace',
      label: 'New workspace',
      section: 'Create',
      keywords: ['add', 'create'],
      run: args.onNewWorkspace,
    },
    {
      id: 'open-settings',
      label: 'Workspace settings',
      hint: args.activeWsName ?? undefined,
      section: 'Settings',
      keywords: ['rename', 'image', 'emoji', 'delete'],
      run: args.onOpenSettings,
    },
    {
      id: 'open-telegram',
      label: 'Telegram delivery',
      section: 'Settings',
      keywords: ['notifications', 'bot', 'dm'],
      run: args.onOpenTelegram,
    },
    {
      id: 'sign-out',
      label: 'Sign out',
      section: 'Account',
      keywords: ['logout', 'log out'],
      run: args.onSignOut,
    },
  ];
  // One Switch-to-Foo entry per workspace (excluding the active one).
  for (const ws of args.workspaces) {
    cmds.push({
      id: `switch-ws-${ws.ws_id}`,
      label: `Switch to "${ws.name}"`,
      hint: `/w/${ws.slug}`,
      section: 'Workspaces',
      keywords: ['switch', 'change'],
      run: () => args.onSwitchWorkspace(ws.ws_id),
    });
  }
  return cmds;
}
